(() => {
  const MOBILE_QUERY = '(max-width: 768px)';
  const STICKY_SELECTOR = '.sticky-atc-wrapper';
  const REFERENCE_STICKY_SELECTOR = '[data-submission-reference-sticky]';
  const STICKY_BUTTON_SELECTOR = '[data-submission-sticky-atc]';
  const STICKY_HOST_SELECTOR = 'section-store--sticky-atc-block';
  const PRIMARY_PURCHASE_SELECTOR = '[data-pdp-purchase-trigger]';
  const ENHANCED_PDP_SELECTOR = '.product-section--enhanced-pdp';
  const REFERENCE_PDP_SELECTOR = '.product-section--reference-pdp';
  const BUNDLE_BLOCK_SELECTOR = 'section-store--bundle-block';
  const SHIPPING_BLOCK_SELECTOR = 'section-store--shipping-info-block';
  const PAYMENT_BLOCK_SELECTOR = 'section-store--payment-icons';
  const BADGE_BLOCK_SELECTOR = 'section-store--badge-block';
  const BENEFITS_BLOCK_SELECTOR = 'section-store--benefits-list-block';
  const BANNER_BLOCK_SELECTOR = 'section-store--banner-block';
  const PREORDER_BADGE_SELECTOR = [
    '.timesact-badge-ribbon-preorder',
    '.timesact-badge-common-preorder',
  ].join(', ');
  const BUNDLE_GATE_CLASS = 'submission-bundle-selection-required';
  const BUNDLE_GATE_MESSAGE_ID = 'SubmissionBundleGateStatus';
  const BUNDLE_INPUT_SELECTOR = [
    '.bundle-option-input[type="radio"]',
    'input[type="radio"][name="bundle_option_id"]',
  ].join(', ');
  const BUNDLE_BRIDGE_MARKER = '__submissionTimesactBridge';
  const SECTION_STORE_QUANTITY_SELECTOR = '[data-section-store-bundle-quantity="true"]';
  const NATIVE_PRODUCT_QUANTITY_SELECTOR = 'input[name="quantity"]';
  const MAX_MATCHED_BUNDLE_QUANTITY = 4;
  const REFERENCE_BUNDLE_INITIALIZED_PROPERTY = '__submissionReferenceBundleInitialized';
  const NATIVE_PRODUCT_QUANTITY_BUTTON_SELECTOR = [
    '.product-block--quantity .js-qty__adjust--minus',
    '.product-block--quantity .js-qty__adjust--plus',
  ].join(', ');
  const KLAVIYO_TEASER_CANDIDATE_SELECTOR = '.needsclick, [data-testid^="klaviyo-form-"]';
  const RELEVANT_MUTATION_SELECTOR = [
    STICKY_SELECTOR,
    STICKY_HOST_SELECTOR,
    PRIMARY_PURCHASE_SELECTOR,
    BUNDLE_BLOCK_SELECTOR,
    SHIPPING_BLOCK_SELECTOR,
    PAYMENT_BLOCK_SELECTOR,
    BADGE_BLOCK_SELECTOR,
    BENEFITS_BLOCK_SELECTOR,
    BANNER_BLOCK_SELECTOR,
    PREORDER_BADGE_SELECTOR,
    KLAVIYO_TEASER_CANDIDATE_SELECTOR,
  ].join(', ');
  const TEASER_CLASS = 'submission-klaviyo-teaser';
  const TEASER_HOST_CLASS = 'submission-klaviyo-teaser-host';
  const OFFSET_PROPERTY = '--submission-sticky-atc-offset';
  const GAP = 8;

  const mobileViewport = window.matchMedia(MOBILE_QUERY);
  let frame;
  let lastProductAddedAt = 0;
  let stickyResizeObserver;
  let observedSticky;
  let productVariantsById;
  let timesactVariantsById;
  let timesactConfigPromise;
  let timesactConfigStatus = 'idle';
  let timesactSettingsById;
  let timesactSettingsPromise;
  let selectedVariantId;
  let hasExplicitVariantSelection = false;
  let observedBundleBlock;
  let observedBundleRoot;
  let bundleMutationObserver;
  let bundleChangeHandler;
  let bundleHydrationTimer;
  let stickyVisibilityHost;
  let stickyVisibilityObserver;
  let stickyVisibilityPrimary;
  let stickyVisibilityUsesFullViewport;
  let stickyVisibilityWrapper;
  let purchaseProxyBypassActive = false;
  let bundleQuantitySyncActive = false;

  function getThemeString(key, fallback) {
    const value = window.theme?.strings?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  function getThemeSetting(key, fallback) {
    const value = window.theme?.settings?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  function isVisible(element) {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) > 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function isKlaviyoTeaser(element) {
    if (element.querySelector('input, textarea, select')) return false;

    const rect = element.getBoundingClientRect();
    const text = element.textContent.replace(/\s+/g, ' ').trim();
    const wasIdentifiedAsTeaser = element.classList.contains(TEASER_CLASS);
    const sitsNearBottom = rect.bottom >= window.innerHeight - 220;
    const sitsNearRight = rect.right >= window.innerWidth - 180;

    if (wasIdentifiedAsTeaser) {
      return /\boff\b/i.test(text);
    }

    return (
      isVisible(element) &&
      rect.width <= 260 &&
      rect.height <= 260 &&
      (wasIdentifiedAsTeaser || (sitsNearBottom && sitsNearRight)) &&
      /\boff\b/i.test(text)
    );
  }

  function findKlaviyoTeaser(candidates) {
    const existingTeaser = candidates.find((element) => element.classList.contains(TEASER_CLASS));
    if (existingTeaser && isKlaviyoTeaser(existingTeaser)) return existingTeaser;

    return candidates
      .filter(isKlaviyoTeaser)
      .sort((first, second) => {
        const firstRect = first.getBoundingClientRect();
        const secondRect = second.getBoundingClientRect();
        return (secondRect.width * secondRect.height) - (firstRect.width * firstRect.height);
      })[0];
  }

  function suppressKlaviyoTeaser(teaser) {
    document.querySelectorAll(`.${TEASER_CLASS}`)
      .forEach((element) => element.classList.toggle(TEASER_CLASS, element === teaser));
    teaser?.classList.add(TEASER_CLASS);
    if (!teaser) return null;

    const animatedTeaser = teaser.closest?.('[data-testid="animated-teaser"]');
    const teaserHost = teaser.closest?.('[class*="kl-teaser-"]')
      || animatedTeaser?.parentElement;
    if (!teaserHost) return null;

    if (teaserHost.contains?.(document.activeElement)) {
      document.activeElement?.blur?.();
    }

    teaserHost.classList.add(TEASER_HOST_CLASS);
    teaserHost.setAttribute?.('aria-hidden', 'true');
    teaserHost.inert = true;
    return teaserHost;
  }

  function centerProductBadges() {
    document.querySelectorAll('section-store--badge-block').forEach((badgeBlock) => {
      const badgeBody = badgeBlock.shadowRoot?.querySelector('.body');
      if (!badgeBody) return;

      badgeBody.style.setProperty('--card-horizontal-align', 'center', 'important');
      badgeBody.style.setProperty('--card-align', 'center', 'important');
    });
  }

  function getDirectProductBlockChild(productBlocks, element) {
    let child = element;

    while (child?.parentElement && child.parentElement !== productBlocks) {
      child = child.parentElement;
    }

    return child?.parentElement === productBlocks ? child : null;
  }

  function placeReferencePdpProof(productBlocks) {
    const productSection = getMainProductSection();
    const proofSlot = productSection?.querySelector('[data-pdp-reference-proof]');
    if (!proofSlot) return false;

    const reviewBadge = productSection.querySelector('.jdgm-preview-badge');
    const reviewWrapper = (
      reviewBadge?.closest?.('[data-pdp-proof-reviews]')
      || getDirectProductBlockChild(productBlocks, reviewBadge)
    );
    const proofLine = productSection.querySelector('[data-pdp-proof-line]');

    if (reviewWrapper && reviewWrapper.parentElement !== proofSlot) {
      reviewWrapper.setAttribute('data-pdp-proof-reviews', '');
      proofSlot.appendChild(reviewWrapper);
    }
    if (proofLine && proofLine.parentElement !== proofSlot) {
      proofSlot.appendChild(proofLine);
    }

    proofSlot.classList.toggle(
      'pdp-reference-proof--ready',
      Boolean(reviewWrapper || proofLine),
    );
    return Boolean(reviewWrapper || proofLine);
  }

  function placePriceWithProductProof() {
    const productSection = getMainProductSection();
    const productBlocks = productSection?.querySelector('[data-product-blocks]');
    if (productSection?.classList?.contains('product-section--enhanced-pdp')) {
      return placeReferencePdpProof(productBlocks);
    }

    const badgeBlock = productBlocks?.querySelector('section-store--badge-block');
    if (!productBlocks || !badgeBlock) return;

    const badgeWrapper = getDirectProductBlockChild(productBlocks, badgeBlock);
    if (!badgeWrapper) return;

    const existingRow = productBlocks.querySelector('[data-pdp-proof-price-row]');
    if (existingRow) {
      if (existingRow.nextElementSibling !== badgeWrapper) {
        productBlocks.insertBefore(existingRow, badgeWrapper);
      }
      return;
    }

    const priceSummary = productBlocks.querySelector('[data-pdp-price-summary]');
    if (!priceSummary) return;

    const proofLine = productBlocks.querySelector('[data-pdp-proof-line]');
    const reviewBadge = productBlocks.querySelector('.jdgm-preview-badge');
    const reviewWrapper = getDirectProductBlockChild(productBlocks, reviewBadge);

    if (proofLine?.parentElement === productBlocks && reviewWrapper) {
      const proofPriceRow = document.createElement('div');
      proofPriceRow.className = 'pdp-proof-price-row';
      proofPriceRow.setAttribute('data-pdp-proof-price-row', '');
      reviewWrapper.setAttribute('data-pdp-proof-reviews', '');

      productBlocks.insertBefore(proofPriceRow, badgeWrapper);
      proofPriceRow.appendChild(reviewWrapper);
      proofPriceRow.appendChild(proofLine);
      proofPriceRow.appendChild(priceSummary);
      return;
    }

    if (priceSummary.nextElementSibling === badgeWrapper) return;
    productBlocks.insertBefore(priceSummary, badgeWrapper);
  }

  function getMainProductSection() {
    return document.querySelector('.product-section[data-history="true"][data-product-id]')
      || [...document.querySelectorAll('.product-section[data-product-id]')]
        .find((section) => section.querySelector('.product-single__form'));
  }

  function isReferencePdp(productSection = getMainProductSection()) {
    return Boolean(productSection?.classList?.contains('product-section--reference-pdp'));
  }

  function isEnhancedPdp(productSection = getMainProductSection()) {
    return Boolean(productSection?.classList?.contains('product-section--enhanced-pdp'));
  }

  function getStickyWrapper() {
    const sticky = document.querySelector(REFERENCE_STICKY_SELECTOR)
      || document.querySelector(STICKY_SELECTOR);

    if (
      sticky?.hasAttribute?.('data-submission-reference-sticky')
      && document.body
      && sticky.parentElement !== document.body
    ) {
      document.body.appendChild(sticky);
    }

    return sticky;
  }

  function ensureReferenceShadowStyle(host, marker, css) {
    const shadowRoot = host?.shadowRoot;
    if (!shadowRoot || shadowRoot.querySelector(`[${marker}]`)) return false;

    const style = document.createElement('style');
    style.setAttribute(marker, '');
    style.textContent = css;
    shadowRoot.appendChild(style);
    return true;
  }

  function syncReferencePackCarouselControls(controls, options) {
    if (!controls || !options) return;

    const previous = controls.querySelector('[data-submission-pack-scroll="prev"]');
    const next = controls.querySelector('[data-submission-pack-scroll="next"]');
    const maxScroll = Math.max(0, options.scrollWidth - options.clientWidth);
    const hasOverflow = maxScroll > 2;

    controls.hidden = !hasOverflow;
    if (previous) previous.disabled = !hasOverflow || options.scrollLeft <= 2;
    if (next) next.disabled = !hasOverflow || options.scrollLeft >= maxScroll - 2;
  }

  function enhanceReferencePackCarousel(bundleBlock) {
    const shadowRoot = bundleBlock?.shadowRoot;
    const options = shadowRoot?.querySelector('.bundle-options');
    if (!shadowRoot || !options) return null;

    let controls = shadowRoot.querySelector('[data-submission-pack-carousel-controls]');
    if (controls?.__submissionPackTrack !== options) {
      controls?.remove?.();
      controls = document.createElement('div');
      controls.className = 'submission-pack-carousel-controls';
      controls.setAttribute('data-submission-pack-carousel-controls', '');

      [
      {
        direction: 'prev',
        label: getThemeString('pdpPreviousPackOptions', 'Previous pack options'),
        path: 'm15 18-6-6 6-6',
      },
      {
        direction: 'next',
        label: getThemeString('pdpNextPackOptions', 'Next pack options'),
        path: 'm9 18 6-6-6-6',
      },
      ].forEach(({ direction, label, path }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'submission-pack-carousel-arrow';
        button.setAttribute('data-submission-pack-scroll', direction);
        button.setAttribute('aria-label', label);
        button.innerHTML = `
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="${path}" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.4"></path>
          </svg>
        `;
        controls.appendChild(button);
      });

      controls.__submissionPackTrack = options;
      controls.addEventListener('click', (event) => {
        const button = event.target.closest?.('[data-submission-pack-scroll]');
        if (!button || button.disabled) return;

        options.scrollBy({
          behavior: 'smooth',
          left: (button.getAttribute('data-submission-pack-scroll') === 'prev' ? -1 : 1)
            * options.clientWidth,
        });
      });
      options.addEventListener(
        'scroll',
        () => syncReferencePackCarouselControls(controls, options),
        { passive: true },
      );
      options.parentElement?.insertBefore(controls, options);
    }

    syncReferencePackCarouselControls(controls, options);
    return controls;
  }

  function styleReferenceBundle(bundleBlock) {
    if (!isEnhancedPdp() || !bundleBlock?.shadowRoot) return;

    ensureReferenceShadowStyle(
      bundleBlock,
      'data-submission-reference-bundle-style',
      `
        :host {
          container-type: inline-size;
          display: block;
        }

        .body {
          padding: 0 0 4px !important;
          position: relative;
        }

        .bundle-header {
          display: block !important;
          font-size: 15px !important;
          font-weight: 800 !important;
          height: auto !important;
          letter-spacing: .08em;
          line-height: 1.2 !important;
          padding: 0 76px 8px 0 !important;
          text-align: left !important;
          text-transform: uppercase;
        }

        .bundle-header::before,
        .bundle-header::after {
          display: none !important;
        }

        .submission-pack-carousel-controls {
          align-items: center;
          display: flex;
          gap: 6px;
          position: absolute;
          right: 0;
          top: -7px;
          z-index: 3;
        }

        .submission-pack-carousel-controls[hidden] {
          display: none !important;
        }

        .submission-pack-carousel-arrow {
          align-items: center;
          appearance: none;
          background: #ffd400;
          border: 1.5px solid #111;
          border-radius: 50%;
          color: #111;
          display: inline-flex;
          height: 32px;
          justify-content: center;
          padding: 0;
          width: 32px;
        }

        .submission-pack-carousel-arrow svg {
          height: 17px;
          width: 17px;
        }

        .submission-pack-carousel-arrow:focus-visible {
          outline: 2px solid #111;
          outline-offset: 2px;
        }

        .submission-pack-carousel-arrow:disabled {
          background: #efefef;
          border-color: #b8b8b8;
          color: #999;
          cursor: default;
        }

        .bundle-options,
        .bundle-options.bundle-options--vertical_stack {
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: nowrap !important;
          align-items: stretch !important;
          box-sizing: border-box;
          gap: 8px !important;
          max-width: 100%;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          overscroll-behavior-inline: contain;
          padding: 4px 2px 7px !important;
          scroll-padding-inline: 2px;
          scroll-snap-type: x mandatory;
          scrollbar-color: #111 #e5e5e5;
          scrollbar-width: thin;
          touch-action: pan-x pan-y;
          width: 100%;
        }

        .bundle-options::-webkit-scrollbar {
          height: 5px;
        }

        .bundle-options::-webkit-scrollbar-thumb {
          background: #111;
          border-radius: 999px;
        }

        .bundle-options::-webkit-scrollbar-track {
          background: #e5e5e5;
          border-radius: 999px;
        }

        .bundle-option,
        .bundle-option.has-active-badge,
        .bundle-options--has-active-badge > .bundle-option {
          flex: 0 0 calc((100% - 8px) / 2) !important;
          margin-top: 0 !important;
          min-width: 0;
          scroll-snap-align: start;
          scroll-snap-stop: always;
        }

        .bundle-option-radio-label {
          cursor: pointer;
        }

        .bundle-option-box {
          background: #fff !important;
          border-radius: 8px !important;
          justify-content: flex-start;
          min-height: 118px;
          padding: 9px 8px 25px !important;
        }

        .bundle-option-radio-label.has-active-badge .bundle-option-box {
          padding-bottom: 25px !important;
          padding-top: 9px !important;
        }

        .bundle-option-box::before {
          border-color: #d7d7d7 !important;
          border-style: solid !important;
          border-width: 1px !important;
        }

        .bundle-option-input:checked ~ .bundle-option-radio-label .bundle-option-box::before {
          border-color: #111 !important;
          border-width: 2px !important;
        }

        .bundle-option .header {
          align-items: flex-start !important;
          display: grid !important;
          gap: 5px !important;
          grid-template-columns: 18px minmax(0, 1fr) !important;
          width: 100%;
        }

        .bundle-option .header .select-indicator {
          grid-column: 1;
          grid-row: 1 / span 2;
          height: 18px !important;
          margin-top: 2px;
          width: 18px !important;
        }

        .bundle-option .header .info {
          align-items: flex-start !important;
          grid-column: 2;
          width: 100%;
        }

        .bundle-option .header .title-wrapper {
          align-items: flex-start !important;
          flex-direction: column !important;
          gap: 2px !important;
        }

        .bundle-option .header .title {
          font-size: 15px !important;
          font-weight: 800 !important;
          overflow: visible !important;
          text-align: left !important;
          white-space: normal;
        }

        .bundle-option .header .title-badge.active,
        .bundle-option .badge.active {
          background: #ffd400 !important;
          color: #111 !important;
          font-size: 8px !important;
          font-weight: 800 !important;
          letter-spacing: .04em;
          text-transform: uppercase;
        }

        .bundle-option .header .title-badge.active {
          align-self: flex-start;
          margin: 0 0 2px !important;
          order: -1 !important;
          padding: 2px 5px !important;
        }

        .bundle-option .header .subtitle {
          color: #18823b !important;
          font-size: 10px !important;
          padding-top: 2px !important;
          text-align: left !important;
        }

        .bundle-option .header .price {
          align-items: flex-start !important;
          grid-column: 2;
          width: 100%;
        }

        .bundle-option .price-per {
          display: none !important;
        }

        .bundle-option .price-total .discounted {
          color: #111 !important;
          font-size: 18px !important;
          font-weight: 800 !important;
        }

        .bundle-option .price-total .original {
          color: #707070 !important;
          font-size: 11px !important;
        }

        .product-variant-selectors {
          display: none !important;
        }

        .bundle-option-radio-label .badge.active {
          bottom: 6px !important;
          font-size: 8px !important;
          left: auto !important;
          padding: 2px 6px !important;
          right: 7px !important;
          top: auto !important;
          transform: none !important;
        }

        @container (min-width: 520px) {
          .bundle-header {
            padding-right: 0 !important;
          }

          .submission-pack-carousel-controls {
            display: none !important;
          }

          .bundle-options,
          .bundle-options.bundle-options--vertical_stack {
            display: grid !important;
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            overflow: visible !important;
            padding: 0 !important;
            scroll-snap-type: none !important;
          }
        }
      `,
    );

    const heading = bundleBlock.shadowRoot.querySelector('.bundle-header');
    const optionStep = Math.max(
      1,
      (getMainProductSection()
        ?.querySelectorAll('.product-block--variants > .variant-wrapper')
        ?.length || 0) + 1,
    );
    const headingText = `${optionStep}. ${getThemeString('pdpChooseYourPack', 'Choose your pack')}`;
    if (heading && heading.textContent.trim() !== headingText) {
      heading.textContent = headingText;
    }
    enhanceReferencePackCarousel(bundleBlock);
  }

  function styleReferenceShipping(shippingBlock) {
    if (!isEnhancedPdp()) return;

    ensureReferenceShadowStyle(
      shippingBlock,
      'data-submission-reference-shipping-style',
      `
        .body {
          border: 0 !important;
          border-radius: 0 !important;
          box-sizing: border-box;
          font-size: 13px !important;
          gap: 12px !important;
          line-height: 1.35 !important;
          padding: 8px 2px !important;
        }

        .shipping-estimate {
          gap: 8px !important;
        }

        .estimate-content {
          align-items: flex-start !important;
          flex-wrap: wrap;
          font-size: inherit !important;
        }

        .shipping-type {
          text-align: right;
        }

        @media (max-width: 520px) {
          .body {
            align-items: flex-start !important;
          }

          .shipping-estimate {
            flex: 0 1 46%;
            min-width: 0;
          }

          .shipping-type {
            flex: 1 1 54%;
            font-size: 11px !important;
            min-width: 0;
            white-space: nowrap;
          }
        }
      `,
    );

    const productSection = getMainProductSection();
    const shippingType = shippingBlock?.shadowRoot?.querySelector('.shipping-type');
    if (shippingType) {
      const productHandle = productSection?.dataset?.productHandle || '';
      const profile = productSection?.dataset?.pdpProfile || '';
      let shippingText = getThemeSetting(
        'freeShippingShortText',
        'Free Shipping over $69',
      );

      if (profile === 'gift-card') {
        shippingText = getThemeSetting(
          'instantDeliveryText',
          'Instant digital delivery',
        );
      } else if (
        productHandle === 'submission-fishing-co-t-shirt'
        || productHandle === 'slow-pitch-everything-foam-trucker-hat'
      ) {
        shippingText = getThemeSetting(
          'separateShippingText',
          'Ships separately',
        );
      }

      if (shippingType.textContent.trim() !== shippingText) {
        shippingType.textContent = shippingText;
      }
    }
  }

  function styleReferencePayments(paymentBlock) {
    if (!isEnhancedPdp()) return;

    ensureReferenceShadowStyle(
      paymentBlock,
      'data-submission-reference-payment-style',
      `
        .body {
          padding: 8px 0 2px !important;
        }

        .payments.fixed-grid {
          flex-wrap: nowrap !important;
          gap: 5px !important;
          justify-content: center !important;
        }

        .payments.fixed-grid .icon {
          flex: 0 1 38px;
          max-width: 38px;
          min-width: 24px;
          width: auto !important;
        }
      `,
    );
  }

  function markReferencePdpStructure() {
    const productSection = getMainProductSection();
    if (!isEnhancedPdp(productSection)) return false;

    const productBlocks = productSection.querySelector('[data-product-blocks]');
    if (!productBlocks) return false;

    const blockClasses = new Map([
      [BUNDLE_BLOCK_SELECTOR, 'pdp-reference-app--bundle'],
      [SHIPPING_BLOCK_SELECTOR, 'pdp-reference-app--shipping'],
      [PAYMENT_BLOCK_SELECTOR, 'pdp-reference-app--payments'],
      [STICKY_HOST_SELECTOR, 'pdp-reference-app--hidden'],
    ]);
    if (isReferencePdp(productSection)) {
      blockClasses.set(BADGE_BLOCK_SELECTOR, 'pdp-reference-app--hidden');
      blockClasses.set(BENEFITS_BLOCK_SELECTOR, 'pdp-reference-app--hidden');
      blockClasses.set(BANNER_BLOCK_SELECTOR, 'pdp-reference-app--hidden');
    }

    blockClasses.forEach((className, selector) => {
      const host = productSection.querySelector(selector);
      const wrapper = getDirectProductBlockChild(productBlocks, host);
      wrapper?.classList?.add(className);
    });

    const bundleBlock = productSection.querySelector(BUNDLE_BLOCK_SELECTOR);
    const shippingBlock = productSection.querySelector(SHIPPING_BLOCK_SELECTOR);
    const paymentBlock = productSection.querySelector(PAYMENT_BLOCK_SELECTOR);
    styleReferenceBundle(bundleBlock);
    styleReferenceShipping(shippingBlock);
    styleReferencePayments(paymentBlock);

    const authorityForm = getMainProductForm();
    if (authorityForm && isReferencePdp(productSection)) {
      authorityForm.setAttribute('aria-hidden', 'true');
      authorityForm
        .querySelectorAll('button, [href], input, select, textarea, [tabindex]')
        .forEach((control) => control.setAttribute('tabindex', '-1'));
    }

    return true;
  }

  function getMainProductForm() {
    return getMainProductSection()?.querySelector('.product-single__form');
  }

  function getNativeProductQuantityInput(form = getMainProductForm()) {
    if (!form) return null;

    return [
      ...(getMainProductSection()?.querySelectorAll?.(NATIVE_PRODUCT_QUANTITY_SELECTOR) || []),
    ].find((input) => (
      input.dataset?.sectionStoreBundleQuantity !== 'true'
      && (
        input.form === form
        || input.getAttribute?.('form') === form.id
      )
    )) || null;
  }

  function normalizeNativeProductQuantity(input) {
    if (!input) return null;

    const parsedMinimum = Number(input.min || input.getAttribute?.('min'));
    const minimum = Number.isInteger(parsedMinimum) ? Math.max(1, parsedMinimum) : 1;
    const parsedMaximum = Number(input.max || input.getAttribute?.('max'));
    const maximum = Number.isInteger(parsedMaximum) && parsedMaximum >= minimum
      ? parsedMaximum
      : null;
    const parsedQuantity = Number(input.value);
    let quantity = Number.isInteger(parsedQuantity) && parsedQuantity >= minimum
      ? parsedQuantity
      : minimum;

    if (maximum !== null) quantity = Math.min(quantity, maximum);

    input.value = String(quantity);
    return quantity;
  }

  function setSectionStoreQuantity(quantity, form = getMainProductForm()) {
    const sectionStoreQuantity = form?.querySelector(SECTION_STORE_QUANTITY_SELECTOR);
    if (!sectionStoreQuantity || !Number.isInteger(quantity) || quantity < 1) return false;

    sectionStoreQuantity.value = String(quantity);
    return true;
  }

  function getMainProductButton() {
    return getMainProductForm()
      ?.querySelector('[data-add-to-cart], button[name="add"]');
  }

  function getPrimaryPurchaseButton() {
    return getMainProductSection()?.querySelector(PRIMARY_PURCHASE_SELECTOR);
  }

  function getProductVariantsById() {
    if (productVariantsById) return productVariantsById;

    const variantJson = getMainProductSection()?.querySelector('[data-variant-json]');
    if (!variantJson) return {};

    try {
      productVariantsById = JSON.parse(variantJson.textContent)
        .reduce((variants, variant) => {
          variants[String(variant.id)] = variant;
          return variants;
        }, {});
    } catch (error) {
      productVariantsById = {};
    }

    return productVariantsById;
  }

  function getReferenceJigWeight(variant) {
    const optionWeights = (variant?.options || [])
      .flatMap((option) => (
        [...String(option).matchAll(/(?:^|[^\d.])(\d+(?:\.\d+)?)\s*g\b/gi)]
          .map((match) => Number(match[1]))
      ))
      .filter((weight) => Number.isFinite(weight) && weight > 0);
    if (optionWeights.length) return optionWeights[0];

    const variantTitleWeights = [
      ...String(variant?.title || '').matchAll(/(?:^|[^\d.])(\d+(?:\.\d+)?)\s*g\b/gi),
    ].map((match) => Number(match[1]));
    if (variantTitleWeights.length === 1) return variantTitleWeights[0];

    const productTitleWeights = [
      ...String(getMainProductSection()?.dataset?.productTitle || '')
        .matchAll(/(?:^|[^\d.])(\d+(?:\.\d+)?)\s*g\b/gi),
    ].map((match) => Number(match[1]));
    if (productTitleWeights.length === 1) return productTitleWeights[0];

    const shippingWeight = Number(variant?.weight);
    return Number.isFinite(shippingWeight) && shippingWeight > 0
      ? shippingWeight
      : null;
  }

  function syncReferenceJigHookBenefit(variantId = getSelectedVariantId()) {
    const productSection = getMainProductSection();
    if (productSection?.dataset?.pdpProfile !== 'jig') return false;

    const benefit = productSection.querySelector('[data-pdp-jig-hook-benefit]');
    const includedState = benefit?.querySelector('[data-pdp-jig-hook-included]');
    const standardState = benefit?.querySelector('[data-pdp-jig-standard-benefit]');
    if (!benefit || !includedState || !standardState) return false;

    const variant = getProductVariantsById()[String(variantId || '')];
    const selectedWeight = getReferenceJigWeight(variant);
    const hookIsIncluded = Number.isFinite(selectedWeight)
      && selectedWeight > 0
      && selectedWeight < 80;

    includedState.hidden = !hookIsIncluded;
    standardState.hidden = hookIsIncluded;
    benefit.dataset.pdpJigHookState = hookIsIncluded ? 'included' : 'not-included';
    return hookIsIncluded;
  }

  function syncReferenceJigNote() {
    if (!isEnhancedPdp()) return;

    const note = getMainProductSection()?.querySelector('[data-pdp-reference-jig-note]');
    const checkedJig = getMainProductSection()
      ?.querySelector('.pdp-reference-option--jig input[data-variant-input]:checked');
    if (!note || !checkedJig) return;

    const descriptions = {
      Mercenary: note.dataset.mercenaryDescription || 'Slimmer profile with a quicker fall.',
      Sumo: note.dataset.sumoDescription || 'Wider profile with a slower flutter.',
    };
    const name = checkedJig.value;
    const nameTarget = note.querySelector('[data-pdp-reference-jig-name]');
    const descriptionTarget = note.querySelector('[data-pdp-reference-jig-description]');
    const selectedLabel = note.dataset.selectedLabel || '{option} selected:';
    const selectedText = selectedLabel.includes('{option}')
      ? selectedLabel.replace(/\{option\}/g, name)
      : `${name} ${selectedLabel}`;

    if (nameTarget) nameTarget.textContent = selectedText;
    if (descriptionTarget) descriptionTarget.textContent = descriptions[name] || '';
  }

  function getReferenceSelectedOptions() {
    const selections = new Map();
    getMainProductSection()
      ?.querySelectorAll('.variant-input-wrap[data-index] input[data-variant-input]:checked')
      .forEach((input) => {
        const index = Number(String(input.dataset.index || '').replace('option', '')) - 1;
        if (Number.isInteger(index) && index >= 0) {
          selections.set(index, input.value);
        }
      });
    return selections;
  }

  function getReferenceVariantImageUrl(variant) {
    const source = variant?.featured_image?.src
      || variant?.featured_media?.preview_image?.src;
    if (!source) return '';

    try {
      const imageUrl = new URL(source, window.location.origin);
      imageUrl.searchParams.set('width', '220');
      return imageUrl.toString();
    } catch (error) {
      return source;
    }
  }

  function syncReferenceColorTileImages() {
    const productSection = getMainProductSection();
    if (!isEnhancedPdp(productSection)) return;

    const rail = productSection.querySelector('[data-pdp-color-rail]');
    const colorIndex = Number(rail?.dataset.pdpOptionIndex);
    if (!rail || !Number.isInteger(colorIndex)) return;

    const variants = Object.values(getProductVariantsById());
    const selectedOptions = getReferenceSelectedOptions();
    rail.querySelectorAll('[data-pdp-color-tile]').forEach((tile) => {
      const color = tile.dataset.pdpColorValue;
      const candidates = variants.filter((variant) => (
        variant.options?.[colorIndex] === color
        && getReferenceVariantImageUrl(variant)
      ));
      const bestVariant = candidates
        .map((variant) => {
          let score = variant.available ? 10 : 0;
          selectedOptions.forEach((selectedValue, optionIndex) => {
            if (optionIndex !== colorIndex && variant.options?.[optionIndex] === selectedValue) {
              score += 100;
            }
          });
          return { score, variant };
        })
        .sort((first, second) => second.score - first.score)[0]
        ?.variant;
      const image = tile.querySelector('.pdp-color-tile__image');
      const imageUrl = getReferenceVariantImageUrl(bestVariant);

      if (image && imageUrl && image.src !== imageUrl) {
        image.removeAttribute('srcset');
        image.src = imageUrl;
      }
    });
  }

  function revealReferenceColorSelection(input) {
    const tile = input
      ?.closest?.('.variant-input')
      ?.querySelector?.('[data-pdp-color-tile]');
    tile?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  function getSelectedVariantId() {
    if (hasExplicitVariantSelection) return selectedVariantId;

    const masterSelect = getMainProductSection()
      ?.querySelector('.product-single__form select[data-product-select]');
    selectedVariantId = masterSelect?.value ? String(masterSelect.value) : null;
    return selectedVariantId;
  }

  function getTimesactVariants() {
    const productSection = getMainProductSection();
    const currentTimesactProduct = window.ta?.currentTimesactProductData;

    if (
      currentTimesactProduct?.productVariants
      && String(currentTimesactProduct.productId) === String(productSection?.dataset.productId)
    ) {
      return currentTimesactProduct.productVariants;
    }

    return timesactVariantsById;
  }

  function loadTimesactVariants() {
    if (timesactConfigPromise) return timesactConfigPromise;

    const productId = getMainProductSection()?.dataset.productId;
    if (!productId) return Promise.resolve({});

    timesactConfigStatus = 'loading';
    timesactConfigPromise = fetch(`/apps/timesact/config?productId=${encodeURIComponent(productId)}`, {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Timesact config returned ${response.status}`);
        return response.json();
      })
      .then((data) => {
        timesactVariantsById = data?.product?.variants || {};
        timesactConfigStatus = 'ready';
        syncAccuratePurchaseState();
        return timesactVariantsById;
      })
      .catch(() => {
        timesactVariantsById = {};
        timesactConfigStatus = 'failed';
        syncAccuratePurchaseState();
        return timesactVariantsById;
      });

    return timesactConfigPromise;
  }

  function getTimesactSettings() {
    return {
      ...(timesactSettingsById || {}),
      ...(window.ta?.settings || {}),
    };
  }

  function loadTimesactSettings() {
    if (timesactSettingsPromise) return timesactSettingsPromise;

    timesactSettingsPromise = fetch('/apps/timesact/shop/settings', {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Timesact settings returned ${response.status}`);
        return response.json();
      })
      .then((data) => {
        timesactSettingsById = data?.settings || {};
        syncAccuratePurchaseState();
        return timesactSettingsById;
      })
      .catch(() => {
        timesactSettingsById = {};
        syncAccuratePurchaseState();
        return timesactSettingsById;
      });

    return timesactSettingsPromise;
  }

  function getPurchaseLabels() {
    const label = getMainProductButton()?.querySelector('[data-add-to-cart-text]')
      || getPrimaryPurchaseButton()?.querySelector('[data-pdp-purchase-text]');

    return {
      addToCart: label?.dataset.defaultText
        || getThemeString('pdpAddToCart', 'Add to cart'),
      preorder: label?.dataset.preorderText
        || getThemeString('pdpPreorder', 'Preorder'),
      soldOut: label?.dataset.soldOutText
        || getThemeString('soldOut', 'Sold out'),
      unavailable: label?.dataset.unavailableText
        || getThemeString('unavailable', 'Unavailable'),
    };
  }

  function getTimesactButtonName(timesactVariant, configKey) {
    const templateId = timesactVariant?.[configKey]?.settingsTemplate;
    const configuredName = templateId
      ? window.ta?.settings?.[templateId]?.button?.name
      : null;
    if (configuredName) return configuredName;

    const mainButtonName = getMainProductButton()
      ?.querySelector('[data-add-to-cart-text]')
      ?.textContent
      ?.replace(/\s+/g, ' ')
      .trim() || null;
    if (!mainButtonName) return null;

    const labels = getPurchaseLabels();
    const normalizedName = mainButtonName.toLowerCase();
    const genericLabels = [
      labels.addToCart,
      labels.soldOut,
      labels.unavailable,
    ].map((label) => label.toLowerCase());

    return genericLabels.includes(normalizedName) ? null : mainButtonName;
  }

  function isTimesactFeatureActive(timesactVariant, enabledKey, configKey) {
    return (
      timesactVariant?.[enabledKey] === true
      && timesactVariant?.[configKey]?.status === 'ACTIVE'
    );
  }

  function getPurchaseState(shopifyVariant, timesactVariant) {
    const labels = getPurchaseLabels();

    if (!shopifyVariant) {
      return {
        key: 'unavailable',
        disabled: true,
        label: labels.unavailable,
      };
    }

    if (isTimesactFeatureActive(timesactVariant, 'isCSEnabled', 'configCS')) {
      return {
        key: 'coming-soon',
        disabled: true,
        label: getTimesactButtonName(timesactVariant, 'configCS')
          || getThemeString('pdpComingSoon', 'Coming soon'),
      };
    }

    if (isTimesactFeatureActive(timesactVariant, 'isBISEnabled', 'configBIS')) {
      return {
        key: 'sold-out',
        disabled: true,
        label: labels.soldOut,
      };
    }

    if (
      (timesactVariant?.isPOEnabled === true && timesactVariant.config?.status === 'NO_STOCK')
      || (timesactVariant?.isISEnabled === true && timesactVariant.configIS?.status === 'NO_STOCK')
    ) {
      return {
        key: 'sold-out',
        disabled: true,
        label: labels.soldOut,
      };
    }

    if (!shopifyVariant.available) {
      return {
        key: 'sold-out',
        disabled: true,
        label: labels.soldOut,
      };
    }

    if (timesactVariant?.isPOEnabled === true && timesactVariant.config?.status === 'ACTIVE') {
      const scopedPreorderLabel = getMainProductSection()?.dataset?.pdpPreorderLabel;
      return {
        key: 'preorder',
        disabled: false,
        label: scopedPreorderLabel
          || getTimesactButtonName(timesactVariant, 'config')
          || labels.preorder,
      };
    }

    if (timesactVariant?.isISEnabled === true && timesactVariant.configIS?.status === 'ACTIVE') {
      return {
        key: 'available',
        disabled: false,
        label: getTimesactButtonName(timesactVariant, 'configIS') || labels.addToCart,
      };
    }

    return {
      key: 'available',
      disabled: false,
      label: labels.addToCart,
    };
  }

  function syncReferencePreorderMediaBadge(state, shopifyVariant) {
    const productSection = getMainProductSection();
    if (!isEnhancedPdp(productSection)) return false;

    const gallery = productSection.querySelector('.product__main-photos');
    const badges = [...(gallery?.querySelectorAll(PREORDER_BADGE_SELECTOR) || [])];
    if (!gallery || !badges.length) return false;

    const isPreorder = String(state?.key || '').startsWith('preorder');
    const featuredMediaId = shopifyVariant?.featured_media?.id;
    const slide = featuredMediaId
      ? [...gallery.querySelectorAll('.product-main-slide[data-media-id]')]
        .find((candidate) => String(candidate.dataset.mediaId) === String(featuredMediaId))
      : null;
    const badgeTarget = slide?.querySelector('.product-image-main') || slide;

    if (!isPreorder || !badgeTarget) {
      badges.forEach((badge) => badge.remove());
      return false;
    }

    const primaryBadge = badges[0];
    badges.slice(1).forEach((badge) => badge.remove());
    if (primaryBadge.parentElement !== badgeTarget) {
      badgeTarget.appendChild(primaryBadge);
    }
    return true;
  }

  function applyPurchaseState(button, state, { updateLabel = true } = {}) {
    if (!button || !state) return;

    if (button.disabled !== state.disabled) button.disabled = state.disabled;
    if (button.classList.contains('disabled') !== state.disabled) {
      button.classList.toggle('disabled', state.disabled);
    }

    const ariaDisabled = String(state.disabled);
    if (button.getAttribute('aria-disabled') !== ariaDisabled) {
      button.setAttribute('aria-disabled', ariaDisabled);
    }
    if (button.dataset.purchaseState !== state.key) {
      button.dataset.purchaseState = state.key;
    }

    const label = button.querySelector(
      '[data-add-to-cart-text], [data-pdp-purchase-text], .sticky-atc-button-text, span',
    );
    if (
      updateLabel
      && label
      && label.textContent.replace(/\s+/g, ' ').trim() !== state.label
    ) {
      label.textContent = state.label;
    }
  }

  function syncAccuratePurchaseState(variantId = getSelectedVariantId()) {
    const sticky = getStickyWrapper();
    const stickyButton = prepareStickyButton(sticky);
    const primaryButton = getPrimaryPurchaseButton();

    syncBundleSelectionGate();
    bindStickyVisibilityToPrimary(sticky);

    const timesactVariants = getTimesactVariants();
    const timesactStateReady = Boolean(timesactVariants)
      || timesactConfigStatus === 'ready'
      || timesactConfigStatus === 'failed';
    if (!timesactStateReady) {
      const loadingState = {
        key: 'loading',
        disabled: true,
        label: getThemeString('pdpLoading', 'Loading...'),
      };
      applyPurchaseState(primaryButton, loadingState);
      applyPurchaseState(stickyButton, loadingState);
      return;
    }

    const normalizedVariantId = variantId ? String(variantId) : null;
    const shopifyVariant = normalizedVariantId
      ? getProductVariantsById()[normalizedVariantId]
      : null;
    const timesactVariant = normalizedVariantId
      ? timesactVariants?.[normalizedVariantId]
      : null;
    let state = getPurchaseState(shopifyVariant, timesactVariant);
    if (
      state.key === 'preorder'
      && !getTimesactMainPreorderMetadataState(timesactVariant).ready
    ) {
      state = {
        key: 'preorder-loading',
        disabled: true,
        label: getThemeString('pdpPreparingPreorder', 'Preparing preorder...'),
      };
    }

    // Symmetry and Timesact exclusively own the native purchase controls.
    // The two independent proxy controls mirror the exact resolved state.
    applyPurchaseState(primaryButton, state);
    applyPurchaseState(stickyButton, state);
    syncReferencePreorderMediaBadge(state, shopifyVariant);
    syncStickyActions(sticky);
  }

  function prepareStickyButton(sticky) {
    if (!sticky) return null;

    const button = sticky.querySelector(
      `${STICKY_BUTTON_SELECTOR}, .sticky-atc-button-section button`,
    );
    if (!button) return null;

    // Section Store searches globally for native ATC attributes whenever
    // Timesact hides the real button. Do not let its clone match that search
    // and become a self-referential purchase button.
    if (!button.hasAttribute('data-submission-sticky-atc')) {
      button.setAttribute('data-submission-sticky-atc', '');
    }
    button.removeAttribute('data-add-to-cart');
    if (button.getAttribute('name') === 'add') button.removeAttribute('name');
    if (button.tagName === 'BUTTON' && button.type !== 'button') {
      button.type = 'button';
    }

    return button;
  }

  function getBundleBlock() {
    return getMainProductSection()?.querySelector(BUNDLE_BLOCK_SELECTOR);
  }

  function ensureBundleAccessibilityStyle(shadowRoot) {
    styleReferenceBundle(getBundleBlock());
    if (!shadowRoot || shadowRoot.querySelector('[data-submission-bundle-a11y-style]')) return;

    const style = document.createElement('style');
    style.dataset.submissionBundleA11yStyle = '';
    style.textContent = `
      .bundle-option-input {
        block-size: 1px !important;
        clip: rect(0 0 0 0) !important;
        clip-path: inset(50%) !important;
        display: block !important;
        inline-size: 1px !important;
        margin: 0 !important;
        opacity: 0 !important;
        overflow: hidden !important;
        padding: 0 !important;
        position: absolute !important;
        white-space: nowrap !important;
      }

      .bundle-option-input:focus + .bundle-option-radio-label {
        outline: 3px solid #005fcc;
        outline-offset: 3px;
      }
    `;
    shadowRoot.appendChild(style);
  }

  function enhanceBundleOptionAccessibility(shadowRoot) {
    if (!shadowRoot) return [];

    ensureBundleAccessibilityStyle(shadowRoot);
    return [...shadowRoot.querySelectorAll(BUNDLE_INPUT_SELECTOR)];
  }

  function getTimesactPeriodDays(period) {
    if (!period) return 1;
    if (period.type === 'WEEK') return Number(period.value) * 7;
    if (period.type === 'MONTH') return Number(period.value) * 30;
    return Number(period.value) || 1;
  }

  function formatTimesactShippingDate(shippingDate, locale) {
    if (!shippingDate || shippingDate.type === 'NO_SET') return 'VARIABLE_NOT_SET';

    if (shippingDate.type === 'DATE') {
      return new Date(Number(shippingDate.date) * 1000)
        .toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    }

    if (shippingDate.type === 'PERIOD') {
      const date = new Date();
      date.setDate(date.getDate() + getTimesactPeriodDays({
        type: shippingDate.periodType,
        value: shippingDate.periodValue,
      }));
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    }

    if (shippingDate.type === 'INTERVAL') {
      const start = new Date();
      const end = new Date();
      start.setDate(start.getDate() + getTimesactPeriodDays(shippingDate.periodStart));
      end.setDate(end.getDate() + getTimesactPeriodDays(shippingDate.periodEnd));
      return [
        start.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }),
        end.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }),
      ].join(' - ');
    }

    return 'VARIABLE_NOT_SET';
  }

  function getTimesactDaysUntilShipping(shippingDate) {
    if (!shippingDate || shippingDate.type === 'NO_SET') return 'VARIABLE_NOT_SET';
    if (shippingDate.type === 'DATE') {
      return Math.ceil(
        (new Date(Number(shippingDate.date) * 1000).getTime() - Date.now())
        / (1000 * 60 * 60 * 24),
      );
    }
    if (shippingDate.type === 'PERIOD') {
      return getTimesactPeriodDays({
        type: shippingDate.periodType,
        value: shippingDate.periodValue,
      });
    }
    if (shippingDate.type === 'INTERVAL') {
      return [
        getTimesactPeriodDays(shippingDate.periodStart),
        getTimesactPeriodDays(shippingDate.periodEnd),
      ].join(' - ');
    }
    return 'VARIABLE_NOT_SET';
  }

  function populateTimesactCartLabel(value, timesactVariant, locale) {
    const config = timesactVariant?.config || {};
    const stock = config.stock || {};
    const preorderQuantity = stock.option === 'STOCK3'
      ? timesactVariant.quantity
      : stock.hasUnlimitedQuantity
        ? 'VARIABLE_NOT_SET'
        : stock.quantity;
    const daysUntilEnd = config.hasEndDate
      ? Math.ceil(
        (new Date(Number(config.endDate) * 1000).getTime() - Date.now())
        / (1000 * 60 * 60 * 24),
      )
      : 'VARIABLE_NOT_SET';

    return String(value)
      .replace('{{preorderQuantity}}', preorderQuantity)
      .replace('{{shippingDate}}', formatTimesactShippingDate(config.shippingDate, locale))
      .replace('{{daysLeftUntilShippingDate}}', getTimesactDaysUntilShipping(config.shippingDate))
      .replace('{{daysLeftUntilPreorderEndDate}}', daysUntilEnd);
  }

  function getTimesactFormMetadata() {
    const form = getMainProductForm();
    const sellingPlanInput = form?.querySelector(
      [
        '[data-timesact-selling-plan][name="selling_plan"]',
        '#timesact_selling_plan_label[name="selling_plan"]',
      ].join(', '),
    );
    const propertyInput = form?.querySelector(
      '#preorder-label[name^="properties["]',
    );
    const propertyKey = propertyInput?.name
      ?.match(/^properties\[(.+)\]$/)?.[1];

    return {
      property: propertyKey
        ? { key: propertyKey, value: propertyInput.value }
        : null,
      sellingPlan: sellingPlanInput?.value || null,
    };
  }

  function getTimesactCartPropertyState(timesactVariant, formMetadata) {
    const templateId = timesactVariant?.config?.settingsTemplate;
    const settings = getTimesactSettings();
    const label = templateId
      ? settings?.[templateId]?.cart?.labelText
      : null;

    if (label?.isEnabled === false) {
      return {
        property: null,
        ready: true,
      };
    }
    if (label?.key && label?.value) {
      const locale = settings?.[templateId]?.message?.locale || 'en-US';
      return {
        property: {
          key: populateTimesactCartLabel(label.key, timesactVariant, locale),
          value: populateTimesactCartLabel(label.value, timesactVariant, locale),
        },
        ready: true,
      };
    }

    if (formMetadata.property) {
      return {
        property: formMetadata.property,
        ready: true,
      };
    }

    return {
      property: null,
      ready: false,
    };
  }

  function getTimesactCartProperty(timesactVariant, formMetadata) {
    return getTimesactCartPropertyState(timesactVariant, formMetadata).property;
  }

  function getTimesactMainPreorderMetadataState(timesactVariant) {
    if (
      timesactVariant?.isPOEnabled !== true
      || timesactVariant.config?.status !== 'ACTIVE'
    ) {
      return { ready: true };
    }

    const formMetadata = getTimesactFormMetadata();
    const expectedSellingPlan = timesactVariant.config?.sellingPlan || null;
    const propertyState = getTimesactCartPropertyState(timesactVariant, formMetadata);
    const sellingPlanReady = expectedSellingPlan
      ? String(formMetadata.sellingPlan) === String(expectedSellingPlan)
      : !formMetadata.sellingPlan;
    const propertyReady = propertyState.ready && (
      propertyState.property
        ? (
          formMetadata.property?.key === propertyState.property.key
          && formMetadata.property?.value === propertyState.property.value
        )
        : !formMetadata.property
    );

    return {
      ready: sellingPlanReady && propertyReady,
    };
  }

  function isSectionStoreProductLine(lineItem) {
    const bundleProperty = lineItem?.properties?.__section_store_bundle;
    if (!bundleProperty) return false;

    try {
      return JSON.parse(bundleProperty).free_gift !== true;
    } catch (error) {
      return true;
    }
  }

  function bridgeTimesactPreorderMetadata(lineItems, originalLineItems = []) {
    if (!Array.isArray(lineItems)) return lineItems;

    const timesactVariants = getTimesactVariants() || {};
    const formMetadata = getTimesactFormMetadata();
    const originalById = new Map(
      originalLineItems.map((lineItem) => [String(lineItem.id), lineItem]),
    );

    lineItems.forEach((lineItem) => {
      if (!isSectionStoreProductLine(lineItem)) return;

      const variantId = String(lineItem.id);
      const timesactVariant = timesactVariants[variantId];
      const isActivePreorder = (
        timesactVariant?.isPOEnabled === true
        && timesactVariant.config?.status === 'ACTIVE'
      );
      const originalLine = originalById.get(variantId);
      const hasCurrentTimesactMetadata = (
        Boolean(formMetadata.sellingPlan)
        && String(originalLine?.selling_plan) === String(formMetadata.sellingPlan)
      );
      if (!isActivePreorder && !hasCurrentTimesactMetadata) return;

      const sellingPlan = timesactVariant?.config?.sellingPlan
        || originalLine?.selling_plan
        || formMetadata.sellingPlan;
      if (sellingPlan) {
        const numericSellingPlan = Number(sellingPlan);
        lineItem.selling_plan = Number.isFinite(numericSellingPlan)
          ? numericSellingPlan
          : sellingPlan;
      }

      const property = getTimesactCartProperty(timesactVariant, formMetadata);
      if (property) {
        lineItem.properties = {
          ...(lineItem.properties || {}),
          [property.key]: property.value,
        };
      }
    });

    return lineItems;
  }

  function patchBundleTimesactBridge(bundleBlock) {
    const interceptor = bundleBlock?.addToCartInterceptor;
    const merge = interceptor?.mergeProductVariantsIntoLineItems;
    if (!interceptor || typeof merge !== 'function') return false;
    if (interceptor[BUNDLE_BRIDGE_MARKER] === merge) return true;

    // Section Store deliberately rebuilds the selected product lines and drops
    // the main form's Timesact fields. Patch its narrow line-builder so its
    // fetch, XHR, and native-form serializers all keep the same metadata.
    const mergeWithTimesactMetadata = function mergeWithTimesactMetadata(
      selectedVariants,
      originalLineItems,
      selectedOption,
    ) {
      const lineItems = merge.call(
        this,
        selectedVariants,
        originalLineItems,
        selectedOption,
      );
      // A shopper-selected Section Store subscription owns its selling plan.
      if (this.bundleBlock?.subscriptionOption?.checked) return lineItems;
      return bridgeTimesactPreorderMetadata(lineItems, originalLineItems);
    };
    interceptor.mergeProductVariantsIntoLineItems = mergeWithTimesactMetadata;
    interceptor[BUNDLE_BRIDGE_MARKER] = mergeWithTimesactMetadata;
    return true;
  }

  function observeBundleBlock(bundleBlock) {
    const shadowRoot = bundleBlock?.shadowRoot;
    const bridgeReady = patchBundleTimesactBridge(bundleBlock);

    if (bundleBlock === observedBundleBlock && shadowRoot === observedBundleRoot) {
      enhanceBundleOptionAccessibility(shadowRoot);
      if (bundleBlock && !bridgeReady && !bundleHydrationTimer) {
        bundleHydrationTimer = window.setTimeout(() => {
          bundleHydrationTimer = undefined;
          scheduleUpdate();
        }, 100);
      }
      return;
    }

    if (bundleHydrationTimer) {
      window.clearTimeout(bundleHydrationTimer);
      bundleHydrationTimer = undefined;
    }
    if (observedBundleRoot && bundleChangeHandler) {
      observedBundleRoot.removeEventListener('click', bundleChangeHandler);
      observedBundleRoot.removeEventListener('change', bundleChangeHandler);
    }
    bundleMutationObserver?.disconnect();

    observedBundleBlock = bundleBlock;
    observedBundleRoot = shadowRoot;
    bundleChangeHandler = undefined;
    bundleMutationObserver = undefined;

    if (!bundleBlock) return;

    if (!shadowRoot) {
      bundleHydrationTimer = window.setTimeout(() => {
        bundleHydrationTimer = undefined;
        scheduleUpdate();
      }, 100);
      return;
    }

    bundleChangeHandler = (event) => {
      const bundleInput = event.target.closest?.(
        '.bundle-option-input, input[name="bundle_option_id"]',
      );
      if (!bundleInput) return;

      if (event.type === 'change' && bundleInput.checked) {
        syncNativeQuantityFromBundleSelection(bundleInput);
      }
      scheduleUpdate();
    };
    shadowRoot.addEventListener('click', bundleChangeHandler);
    shadowRoot.addEventListener('change', bundleChangeHandler);
    enhanceBundleOptionAccessibility(shadowRoot);
    if (!bridgeReady) {
      bundleHydrationTimer = window.setTimeout(() => {
        bundleHydrationTimer = undefined;
        scheduleUpdate();
      }, 100);
    }

    bundleMutationObserver = new MutationObserver(scheduleUpdate);
    bundleMutationObserver.observe(shadowRoot, {
      attributes: true,
      attributeFilter: ['checked'],
      childList: true,
      subtree: true,
    });
  }

  function getBundleSelectionState() {
    const block = getBundleBlock();
    observeBundleBlock(block);

    const inputs = block?.shadowRoot
      ? enhanceBundleOptionAccessibility(block.shadowRoot)
      : [];

    return {
      block,
      inputs,
      hasOptions: inputs.length > 0,
      selected: inputs.some((input) => input.checked),
    };
  }

  function getBundleOptionQuantity(bundleOption) {
    let quantity;

    try {
      quantity = Number(bundleOption?.variantSelectorsCount?.());
    } catch (error) {
      quantity = NaN;
    }

    if (Number.isInteger(quantity) && quantity > 0) return quantity;

    const option = bundleOption?.data?.bundleOption;
    if (option?.discount_strategy === 'bxgy') {
      quantity = (
        Number(option.bxgy_discount?.customer_buy_quantity)
        + Number(option.bxgy_discount?.customer_get_quantity)
      );
    } else {
      quantity = Number(option?.volume_discount?.minimum_quantity);
    }

    return Number.isInteger(quantity) && quantity > 0 ? quantity : null;
  }

  function getBundleQuantityOptions(selection = getBundleSelectionState()) {
    const bundleOptions = [...(selection.block?.bundleOptions || [])];

    return selection.inputs
      .map((input) => {
        const bundleOption = bundleOptions.find((option) => option?.target?.input === input);

        return {
          bundleOption,
          input,
          quantity: getBundleOptionQuantity(bundleOption),
        };
      })
      .filter((option) => option.quantity !== null);
  }

  function syncNativeQuantityFromBundleSelection(bundleInput) {
    if (bundleQuantitySyncActive || !bundleInput?.checked) return false;

    const selection = getBundleSelectionState();
    const selectedOption = getBundleQuantityOptions(selection)
      .find((option) => option.input === bundleInput);
    const nativeQuantity = getNativeProductQuantityInput();
    if (!selectedOption) return false;

    bundleQuantitySyncActive = true;
    try {
      if (nativeQuantity) {
        nativeQuantity.value = String(selectedOption.quantity);
      }
      setSectionStoreQuantity(selectedOption.quantity);
      nativeQuantity?.dispatchEvent?.(new Event('input', { bubbles: true }));
      nativeQuantity?.dispatchEvent?.(new Event('change', { bubbles: true }));
    } finally {
      bundleQuantitySyncActive = false;
    }

    scheduleUpdate();
    return true;
  }

  function deselectBundleQuantityOption(option) {
    const input = option?.input;
    if (!input?.checked) return false;

    const label = option.bundleOption?.target?.radioLabel
      || (
        input.nextElementSibling?.matches?.('.bundle-option-radio-label')
          ? input.nextElementSibling
          : input.closest?.('.bundle-option')?.querySelector?.('.bundle-option-radio-label')
      );

    label?.click?.();
    if (input.checked) {
      input.checked = false;
      option.bundleOption?.handleOptionDeselected?.();
      input.dispatchEvent?.(new Event('change', { bubbles: true }));
    }

    return !input.checked;
  }

  function selectBundleQuantityOption(option, selection) {
    const input = option?.input;
    if (!input || input.disabled || input.checked) return Boolean(input?.checked);

    input.click?.();
    if (!input.checked) {
      selection.inputs.forEach((candidate) => {
        candidate.checked = candidate === input;
      });
      input.dispatchEvent?.(new Event('change', { bubbles: true }));
    }

    return input.checked;
  }

  function syncBundleSelectionFromNativeQuantity() {
    if (bundleQuantitySyncActive) return false;

    const nativeQuantity = getNativeProductQuantityInput();
    const quantity = normalizeNativeProductQuantity(nativeQuantity);
    if (quantity === null) return false;

    const selection = getBundleSelectionState();
    const options = getBundleQuantityOptions(selection);
    if (!options.length) return false;

    const matchingOption = options.find((option) => (
      quantity <= MAX_MATCHED_BUNDLE_QUANTITY
      &&
      option.quantity === quantity
      && !option.input.disabled
    ));
    const selectedOption = options.find((option) => option.input.checked);

    bundleQuantitySyncActive = true;
    try {
      if (matchingOption) {
        selectBundleQuantityOption(matchingOption, selection);
      } else if (selectedOption) {
        deselectBundleQuantityOption(selectedOption);
      }

      setSectionStoreQuantity(quantity);
    } finally {
      bundleQuantitySyncActive = false;
    }

    scheduleUpdate();
    return Boolean(matchingOption || selectedOption);
  }

  function initializeReferenceBundleSelection() {
    if (!isReferencePdp()) return false;

    const selection = getBundleSelectionState();
    const block = selection.block;
    if (!block || block[REFERENCE_BUNDLE_INITIALIZED_PROPERTY]) return false;

    const options = getBundleQuantityOptions(selection);
    if (!options.length) return false;

    block[REFERENCE_BUNDLE_INITIALIZED_PROPERTY] = true;
    if (selection.selected) return true;

    return syncBundleSelectionFromNativeQuantity();
  }

  function getBundlePreorderMetadataState(selection) {
    if (!selection?.selected) return { ready: true };
    if (selection.block?.subscriptionOption?.checked) return { ready: true };

    let selectedVariants;
    try {
      selectedVariants = selection.block?.selectedProductVariants;
    } catch (error) {
      selectedVariants = null;
    }

    const timesactVariants = getTimesactVariants();
    if (!timesactVariants) {
      return {
        ready: timesactConfigStatus === 'failed',
      };
    }
    if (!Array.isArray(selectedVariants) || selectedVariants.length === 0) {
      return {
        ready: timesactConfigStatus === 'failed',
      };
    }

    const formMetadata = getTimesactFormMetadata();
    const activePreorders = selectedVariants
      .map((variant) => timesactVariants[String(variant?.id)])
      .filter((variant) => (
        variant?.isPOEnabled === true
        && variant.config?.status === 'ACTIVE'
      ));
    if (activePreorders.length && !patchBundleTimesactBridge(selection.block)) {
      return { ready: false };
    }
    const missingMetadata = activePreorders.some((variant) => (
      !variant.config?.sellingPlan
      || !getTimesactCartPropertyState(variant, formMetadata).ready
    ));

    return {
      ready: !missingMetadata,
    };
  }

  function getBundleGateMessage(reason) {
    if (reason === 'preorder-metadata') {
      return getThemeString(
        'pdpPreorderDetailsLoading',
        'Preorder details are still loading. Please try again in a moment.',
      );
    }
    return getThemeString(
      'pdpChooseSpecialOffer',
      'Choose a Special Offer above to unlock this button.',
    );
  }

  function ensureBundleGateMessage(mainButton, reason = 'selection') {
    const form = getMainProductForm();
    if (!form || !mainButton) return null;

    let message = form.querySelector('[data-bundle-gate-status]');
    if (message) {
      message.id = BUNDLE_GATE_MESSAGE_ID;
      message.textContent = getBundleGateMessage(reason);
      return message;
    }

    message = document.createElement('p');
    message.className = 'submission-bundle-gate-message';
    message.dataset.bundleGateStatus = '';
    message.id = BUNDLE_GATE_MESSAGE_ID;
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.textContent = getBundleGateMessage(reason);
    mainButton.parentNode.insertBefore(message, mainButton);
    return message;
  }

  function syncBundleSelectionGate() {
    const mainButton = getMainProductButton();
    if (!mainButton) return false;

    const selection = getBundleSelectionState();
    const preorderMetadata = getBundlePreorderMetadataState(selection);
    let gateReason = null;
    if (selection.hasOptions && !selection.selected) {
      gateReason = 'selection';
    } else if (selection.hasOptions && !preorderMetadata.ready) {
      gateReason = 'preorder-metadata';
    }
    const shouldGate = Boolean(gateReason) && !mainButton.disabled;
    const existingMessage = getMainProductForm()
      ?.querySelector('[data-bundle-gate-status]');
    const message = shouldGate
      ? ensureBundleGateMessage(mainButton, gateReason)
      : existingMessage;

    if (mainButton.classList.contains(BUNDLE_GATE_CLASS) !== shouldGate) {
      mainButton.classList.toggle(BUNDLE_GATE_CLASS, shouldGate);
    }

    if (shouldGate) {
      if (mainButton.dataset.bundleSelectionRequired !== 'true') {
        mainButton.dataset.bundleSelectionRequired = 'true';
      }
      if (mainButton.dataset.bundleGateReason !== gateReason) {
        mainButton.dataset.bundleGateReason = gateReason;
      }
      if (mainButton.getAttribute('aria-disabled') !== 'true') {
        mainButton.setAttribute('aria-disabled', 'true');
      }
      const describedBy = new Set(
        (mainButton.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
      );
      describedBy.add(BUNDLE_GATE_MESSAGE_ID);
      mainButton.setAttribute('aria-describedby', [...describedBy].join(' '));
      if (message) message.hidden = false;
    } else {
      if ('bundleSelectionRequired' in mainButton.dataset) {
        delete mainButton.dataset.bundleSelectionRequired;
      }
      if ('bundleGateReason' in mainButton.dataset) {
        delete mainButton.dataset.bundleGateReason;
      }
      const ariaDisabled = String(mainButton.disabled);
      if (mainButton.getAttribute('aria-disabled') !== ariaDisabled) {
        mainButton.setAttribute('aria-disabled', ariaDisabled);
      }
      const describedBy = (mainButton.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== BUNDLE_GATE_MESSAGE_ID);
      if (describedBy.length) {
        mainButton.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        mainButton.removeAttribute('aria-describedby');
      }
      if (message) message.hidden = true;
    }

    return shouldGate;
  }

  function isBundleGateBlocking() {
    const shouldGate = syncBundleSelectionGate();
    const gateReason = getMainProductButton()?.dataset.bundleGateReason;
    return (
      shouldGate
      && (gateReason === 'preorder-metadata' || !purchaseProxyBypassActive)
    );
  }

  function showBundleSelectionRequired() {
    const selection = getBundleSelectionState();
    const gateReason = getMainProductButton()?.dataset.bundleGateReason || 'selection';
    const message = ensureBundleGateMessage(getMainProductButton(), gateReason);

    if (message) {
      message.hidden = false;
      message.textContent = '';
      window.requestAnimationFrame(() => {
        message.textContent = getBundleGateMessage(gateReason);
      });
    }

    if (gateReason === 'preorder-metadata') {
      loadTimesactVariants();
      loadTimesactSettings();
      return;
    }

    selection.block?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    window.setTimeout(() => {
      selection.inputs
        .find((input) => !input.disabled)
        ?.focus({ preventScroll: true });
    }, 350);
  }

  function getSelectableVariantControl() {
    const optionGroups = [...document.querySelectorAll('.variant-input-wrap')]
      .filter((group) => (
        !group.closest(STICKY_SELECTOR) &&
        group.querySelectorAll('input[data-variant-input], input[type="radio"]').length > 1
      ));

    if (optionGroups.length) return optionGroups[0];

    return [...document.querySelectorAll('select[data-variant-input], select.single-option-selector')]
      .find((select) => !select.closest(STICKY_SELECTOR) && select.options.length > 1);
  }

  function syncStickyActions(sticky) {
    if (!sticky) return;

    const changeLink = sticky.querySelector('.sticky-atc-change-link');
    const changeDash = sticky.querySelector('.sticky-atc-variant-dash');
    const nativePicker = sticky.querySelector('[data-submission-sticky-variant-picker]');
    const hasChoices = Boolean(getSelectableVariantControl());
    const choicesHidden = !hasChoices;
    const ariaHidden = String(choicesHidden);

    if (changeLink) {
      if (changeLink.hidden !== choicesHidden) changeLink.hidden = choicesHidden;
      if (changeLink.getAttribute('aria-hidden') !== ariaHidden) {
        changeLink.setAttribute('aria-hidden', ariaHidden);
      }
    }
    if (changeDash && changeDash.hidden !== choicesHidden) {
      changeDash.hidden = choicesHidden;
    }
    if (nativePicker) {
      nativePicker.disabled = choicesHidden;
      nativePicker.hidden = choicesHidden;
      nativePicker.setAttribute('aria-hidden', ariaHidden);
    }
  }

  function selectReferenceVariantFromSticky(picker) {
    const variant = getProductVariantsById()[String(picker?.value || '')];
    const productSection = getMainProductSection();
    if (!picker || !variant || !productSection || !Array.isArray(variant.options)) return false;

    const inputs = variant.options.map((optionValue, optionIndex) => (
      [...productSection.querySelectorAll(
        `.variant-input-wrap[data-index="option${optionIndex + 1}"] input[data-variant-input]`,
      )].find((input) => input.value === String(optionValue))
    ));

    if (!inputs.length || inputs.some((input) => !input || input.disabled)) return false;

    inputs.forEach((input) => {
      input.checked = true;
    });
    inputs[inputs.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function getReferencePurchasePriceText() {
    const selection = getBundleSelectionState();
    const selectedInput = selection.inputs.find((input) => input.checked);
    const selectedLabel = selectedInput?.nextElementSibling?.matches?.('.bundle-option-radio-label')
      ? selectedInput.nextElementSibling
      : selectedInput?.closest?.('.bundle-option')?.querySelector?.('.bundle-option-radio-label');
    const selectedBundlePriceElement = selectedLabel?.querySelector?.('.price-total .discounted')
      || selectedLabel?.querySelector?.('.price-total')
      || selectedLabel?.querySelector?.('.discounted');
    const selectedBundlePrice = selectedBundlePriceElement
      ?.textContent
      ?.replace(/\s+/g, ' ')
      ?.trim();

    if (selectedBundlePrice) return selectedBundlePrice;

    return getMainProductSection()
      ?.querySelector('.product-block--price .product__price .money, .product-block--price .product__price')
      ?.textContent
      ?.replace(/\s+/g, ' ')
      ?.trim() || '';
  }

  function syncReferenceStickyDetails(sticky) {
    if (!isEnhancedPdp() || !sticky) return;

    sticky.classList.add('sticky-atc-wrapper--reference-pdp');
    const productSection = getMainProductSection();
    const variant = getProductVariantsById()[String(getSelectedVariantId() || '')];
    const productName = sticky.querySelector('.sticky-atc-product-name');
    const variantContainer = sticky.querySelector('.sticky-atc-variant-container');
    const variantName = sticky.querySelector('.sticky-atc-variant-name');
    const nativePicker = sticky.querySelector('[data-submission-sticky-variant-picker]');
    const image = sticky.querySelector('.sticky-atc-product-image');
    const variantTitle = variant?.title?.replace(/\s+/g, ' ').trim() || '';
    const hasVariantTitle = Boolean(variantTitle && variantTitle !== 'Default Title');

    if (
      productName
      && productName.textContent !== productSection?.dataset.productTitle
    ) {
      productName.textContent = productSection?.dataset.productTitle || '';
    }
    if (variantName && variantName.textContent !== variantTitle) {
      variantName.textContent = variantTitle;
    }
    if (nativePicker && variant?.id && nativePicker.value !== String(variant.id)) {
      nativePicker.value = String(variant.id);
    }
    if (variantContainer) {
      variantContainer.hidden = !hasVariantTitle;
      variantContainer.style.display = hasVariantTitle ? 'flex' : 'none';
    }

    const imageUrl = getReferenceVariantImageUrl(variant);
    if (image && imageUrl && image.src !== imageUrl) {
      image.removeAttribute('srcset');
      image.src = imageUrl;
    }

    const buttonSection = sticky.querySelector('.sticky-atc-button-section');
    const button = buttonSection?.querySelector('button');
    if (!buttonSection || !button) return;

    let price = buttonSection.querySelector('[data-submission-sticky-price]');
    if (!price) {
      price = document.createElement('span');
      price.className = 'submission-sticky-atc-price';
      price.setAttribute('data-submission-sticky-price', '');
      buttonSection.insertBefore(price, button);
    }

    const priceText = getReferencePurchasePriceText();
    if (price.textContent !== priceText) price.textContent = priceText;
    price.hidden = !priceText;
  }

  function openVariantChoices(changeLink) {
    const variantControl = getSelectableVariantControl();

    if (!variantControl) {
      changeLink.hidden = true;
      const changeDash = changeLink
        .closest('.sticky-atc-variant-container')
        ?.querySelector('.sticky-atc-variant-dash');
      if (changeDash) changeDash.hidden = true;
      return;
    }

    variantControl.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    const focusTarget = variantControl.matches('select')
      ? variantControl
      : variantControl.querySelector('input:not([disabled]), button:not([disabled]), select:not([disabled])');

    window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 350);
  }

  function syncSectionStoreSinglePurchaseQuantity() {
    const selection = getBundleSelectionState();
    if (selection.selected) return false;

    const form = getMainProductForm();
    const nativeQuantity = getNativeProductQuantityInput(form);
    if (nativeQuantity) nativeQuantity.value = '1';

    const sectionStoreUpdated = setSectionStoreQuantity(1, form);
    return Boolean(nativeQuantity || sectionStoreUpdated);
  }

  function submitFromPurchaseProxy(proxyButton) {
    const mainButton = getMainProductButton();
    if (!proxyButton || !mainButton || mainButton.disabled || proxyButton.disabled) return;

    syncSectionStoreSinglePurchaseQuantity();
    purchaseProxyBypassActive = true;
    proxyButton.disabled = true;
    proxyButton.classList.add('btn--loading');
    try {
      mainButton.click();
    } catch (error) {
      purchaseProxyBypassActive = false;
      proxyButton.classList.remove('btn--loading');
      proxyButton.disabled = mainButton.disabled;
      proxyButton.classList.toggle('disabled', mainButton.disabled);
      proxyButton.setAttribute('aria-disabled', String(mainButton.disabled));
      throw error;
    }
    window.setTimeout(() => {
      purchaseProxyBypassActive = false;
    }, 0);
    window.setTimeout(() => {
      proxyButton.classList.remove('btn--loading');
      proxyButton.disabled = mainButton.disabled;
      proxyButton.classList.toggle('disabled', mainButton.disabled);
      proxyButton.setAttribute('aria-disabled', String(mainButton.disabled));
      syncAccuratePurchaseState();
    }, 750);
  }

  function observeSticky(sticky) {
    if (sticky === observedSticky) return;

    stickyResizeObserver?.disconnect();
    observedSticky = sticky;

    if (sticky && 'ResizeObserver' in window) {
      stickyResizeObserver = new ResizeObserver(scheduleUpdate);
      stickyResizeObserver.observe(sticky);
    }
  }

  function setStickyThresholdVisibility(sticky, shouldShow) {
    const cartIsOpen = Boolean(
      document.documentElement?.classList?.contains?.('js-drawer-open')
      || document.querySelector('#CartDrawer.drawer--is-open'),
    );
    const isShown = shouldShow && !cartIsOpen;
    const stickyBox = sticky.querySelector('.sticky-atc-box');

    sticky.classList.toggle('sticky-atc-wrapper--visible', isShown);
    sticky.style.opacity = isShown ? '1' : '0';
    sticky.style.pointerEvents = isShown ? 'auto' : 'none';
    sticky.setAttribute?.('aria-hidden', String(!isShown));
    stickyBox?.classList.toggle('hidden', !isShown);
  }

  function getViewportBounds() {
    return {
      bottom: window.innerHeight,
      left: 0,
      right: window.innerWidth,
      top: 0,
    };
  }

  function isRectFullyVisible(rect, viewport = getViewportBounds()) {
    if (!rect || !viewport) return false;

    const width = Number.isFinite(rect.width) ? rect.width : rect.right - rect.left;
    const height = Number.isFinite(rect.height) ? rect.height : rect.bottom - rect.top;

    return (
      width > 0
      && height > 0
      && rect.top >= viewport.top
      && rect.left >= viewport.left
      && rect.bottom <= viewport.bottom
      && rect.right <= viewport.right
    );
  }

  function bindStickyVisibilityToPrimary(sticky) {
    const host = document.querySelector(STICKY_HOST_SELECTOR);
    const primaryButton = getPrimaryPurchaseButton();
    const themeOwnedSticky = sticky?.hasAttribute?.('data-submission-reference-sticky');
    const useFullViewportVisibility = Boolean(themeOwnedSticky && mobileViewport.matches);

    if (
      !sticky
      || (!host && !themeOwnedSticky)
      || !primaryButton
      || typeof IntersectionObserver !== 'function'
    ) {
      return;
    }

    if (
      host === stickyVisibilityHost
      && sticky === stickyVisibilityWrapper
      && primaryButton === stickyVisibilityPrimary
      && useFullViewportVisibility === stickyVisibilityUsesFullViewport
      && (
        themeOwnedSticky
        || (
          host?.originalBtn === primaryButton
          && host?.intersectionObserver === stickyVisibilityObserver
        )
      )
    ) {
      return;
    }

    try {
      if (!themeOwnedSticky) {
        host.intersectionObserver?.disconnect?.();
        host.originalBtn = primaryButton;
        host.setupSync?.();
        host.originalBtn = primaryButton;
      } else {
        stickyVisibilityObserver?.disconnect?.();
      }

      stickyVisibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== primaryButton) return;

          if (useFullViewportVisibility) {
            setStickyThresholdVisibility(
              sticky,
              !isRectFullyVisible(
                entry.boundingClientRect,
                entry.rootBounds || getViewportBounds(),
              ),
            );
            return;
          }

          const triggerLine = entry.rootBounds?.bottom ?? window.innerHeight / 3;
          setStickyThresholdVisibility(
            sticky,
            entry.boundingClientRect.top <= triggerLine,
          );
        });
      }, useFullViewportVisibility
        ? {
          rootMargin: '0px',
          threshold: [0, 1],
        }
        : {
          rootMargin: '0px 0px -66.6667% 0px',
          threshold: 0,
        });
      stickyVisibilityObserver.observe(primaryButton);
      if (!themeOwnedSticky) host.intersectionObserver = stickyVisibilityObserver;
    } catch (error) {
      stickyVisibilityObserver?.disconnect?.();
      stickyVisibilityHost = undefined;
      stickyVisibilityObserver = undefined;
      stickyVisibilityPrimary = undefined;
      stickyVisibilityUsesFullViewport = undefined;
      stickyVisibilityWrapper = undefined;
      return;
    }

    stickyVisibilityHost = host;
    stickyVisibilityPrimary = primaryButton;
    stickyVisibilityUsesFullViewport = useFullViewportVisibility;
    stickyVisibilityWrapper = sticky;
  }

  function syncStickyThresholdVisibility(sticky) {
    const primaryButton = getPrimaryPurchaseButton();
    if (!sticky || !primaryButton) return;

    const useFullViewportVisibility = Boolean(
      sticky.hasAttribute?.('data-submission-reference-sticky')
      && mobileViewport.matches
    );
    const primaryRect = primaryButton.getBoundingClientRect();
    const primaryIsCompletelyOnScreen = isRectFullyVisible(primaryRect, getViewportBounds());

    setStickyThresholdVisibility(
      sticky,
      useFullViewportVisibility
        ? !primaryIsCompletelyOnScreen
        : primaryRect.top <= window.innerHeight / 3,
    );
  }

  function update() {
    frame = undefined;

    const candidates = [...document.querySelectorAll(KLAVIYO_TEASER_CANDIDATE_SELECTOR)];
    const teaser = findKlaviyoTeaser(candidates);
    suppressKlaviyoTeaser(teaser);

    const sticky = getStickyWrapper();
    observeSticky(sticky);
    prepareStickyButton(sticky);
    bindStickyVisibilityToPrimary(sticky);
    syncStickyThresholdVisibility(sticky);
    markReferencePdpStructure();
    observeBundleBlock(getBundleBlock());
    initializeReferenceBundleSelection();
    syncAccuratePurchaseState();
    syncStickyActions(sticky);
    syncReferenceStickyDetails(sticky);
    syncReferenceJigNote();
    syncReferenceJigHookBenefit();
    syncReferenceColorTileImages();
    placePriceWithProductProof();
    centerProductBadges();

    let offset = 0;
    if (mobileViewport.matches && sticky && isVisible(sticky)) {
      const stickyRect = sticky.getBoundingClientRect();
      offset = Math.max(0, Math.ceil(window.innerHeight - stickyRect.top + GAP));
    }

    const offsetValue = `${offset}px`;
    if (document.documentElement.style.getPropertyValue(OFFSET_PROPERTY) !== offsetValue) {
      document.documentElement.style.setProperty(OFFSET_PROPERTY, offsetValue);
    }
  }

  function scheduleUpdate() {
    if (frame) return;
    frame = window.requestAnimationFrame(update);
  }

  function mutationTouchesRelevantUi(mutation) {
    const target = mutation.target?.nodeType === 1
      ? mutation.target
      : mutation.target?.parentElement;

    if (target?.closest?.(
      `${STICKY_SELECTOR}, ${BUNDLE_BLOCK_SELECTOR}, .product-single__form`,
    )) {
      return true;
    }

    return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => (
      node.nodeType === 1
      && (
        node.matches(RELEVANT_MUTATION_SELECTOR)
        || node.querySelector(RELEVANT_MUTATION_SELECTOR)
      )
    ));
  }

  const mutationObserver = new MutationObserver((mutations) => {
    if (mutations.some(mutationTouchesRelevantUi)) scheduleUpdate();
  });
  mutationObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'disabled'],
    childList: true,
    subtree: true,
  });

  mobileViewport.addEventListener?.('change', scheduleUpdate);
  document.addEventListener('click', (event) => {
    const timesactModalClose = event.target.closest('#mixed-modal .md-close, .md-overlay');
    if (timesactModalClose) {
      const cartIsOpen = Boolean(document.querySelector('#CartDrawer.drawer--is-open'));
      const productWasJustAdded = Date.now() - lastProductAddedAt < 10000;

      if (cartIsOpen || productWasJustAdded) {
        window.setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }, 50);
      }
      return;
    }

    const mainPurchaseButton = event.target.closest(
      '.product-single__form [data-add-to-cart], .product-single__form button[name="add"]',
    );
    if (mainPurchaseButton === getMainProductButton() && isBundleGateBlocking()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showBundleSelectionRequired();
      return;
    }

    const stickyChangeLink = event.target.closest(`${STICKY_SELECTOR} .sticky-atc-change-link`);
    if (stickyChangeLink) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openVariantChoices(stickyChangeLink);
      return;
    }

    const stickyAddButton = event.target.closest(
      `${STICKY_SELECTOR} ${STICKY_BUTTON_SELECTOR}, ${STICKY_SELECTOR} .sticky-atc-button-section button`,
    );
    if (stickyAddButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitFromPurchaseProxy(
        prepareStickyButton(stickyAddButton.closest(STICKY_SELECTOR)) || stickyAddButton,
      );
      return;
    }

    const primaryPurchaseButton = event.target.closest(PRIMARY_PURCHASE_SELECTOR);
    if (!primaryPurchaseButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    submitFromPurchaseProxy(primaryPurchaseButton);
  }, true);
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.(NATIVE_PRODUCT_QUANTITY_BUTTON_SELECTOR)) return;
    syncBundleSelectionFromNativeQuantity();
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-submission-sticky-variant-picker]')) return;

    if (!selectReferenceVariantFromSticky(event.target)) {
      syncReferenceStickyDetails(event.target.closest(STICKY_SELECTOR));
    }
  });
  document.addEventListener('change', (event) => {
    if (event.target !== getNativeProductQuantityInput()) return;
    syncBundleSelectionFromNativeQuantity();
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('.product-section--enhanced-pdp input[data-variant-input]')) return;

    window.requestAnimationFrame(() => {
      syncReferenceJigNote();
      syncReferenceColorTileImages();
      revealReferenceColorSelection(
        event.target.matches('.pdp-color-tile__input') ? event.target : null,
      );
    });
  });
  document.addEventListener('focusin', (event) => {
    if (!event.target.matches?.('.pdp-color-tile__input')) return;
    revealReferenceColorSelection(event.target);
  });
  document.addEventListener('submit', (event) => {
    if (
      event.target === getMainProductForm()
      && isBundleGateBlocking()
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showBundleSelectionRequired();
    }
  }, true);
  document.addEventListener('ajaxProduct:added', () => {
    lastProductAddedAt = Date.now();
    scheduleUpdate();
  });
  document.addEventListener('cart:open', scheduleUpdate);
  document.addEventListener('cart:close', scheduleUpdate);
  document.addEventListener('variant:change', (event) => {
    hasExplicitVariantSelection = true;
    selectedVariantId = event.detail?.variant?.id
      ? String(event.detail.variant.id)
      : null;
    const sticky = getStickyWrapper();
    prepareStickyButton(sticky);
    window.requestAnimationFrame(() => {
      syncAccuratePurchaseState(selectedVariantId);
      syncReferenceStickyDetails(sticky);
      syncReferenceJigHookBenefit(selectedVariantId);
    });
  });
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('load', scheduleUpdate, { once: true });
  [
    BUNDLE_BLOCK_SELECTOR,
    SHIPPING_BLOCK_SELECTOR,
    PAYMENT_BLOCK_SELECTOR,
    BADGE_BLOCK_SELECTOR,
    BENEFITS_BLOCK_SELECTOR,
    BANNER_BLOCK_SELECTOR,
  ].forEach((selector) => {
    window.customElements?.whenDefined?.(selector)?.then(scheduleUpdate);
  });
  window.customElements?.whenDefined?.(STICKY_HOST_SELECTOR)?.then(scheduleUpdate);
  loadTimesactVariants();
  loadTimesactSettings();
  scheduleUpdate();
})();
